import { useLightMode } from '@/hooks/useLightMode'

import { Box, CloseIcon, Flex, IconButton } from '@hivewatch/dark'

import { getNotificationToastTokens } from '../styles/tokens'
import { NotificationIcon } from './NotificationIcon'
import { notificationToastShellStyles } from './NotificationToastShell.styles'

interface NotificationToastShellIProps {
  /** Severity accent for the left bar + icon (a dark colour token). */
  accentColor: string
  /** Timestamp passed back to onCancel to dismiss this toast. */
  notificationTimestamp: string
  onCancel?: (incidentTimeStamp: string) => void
  /** Card height — audio toasts are taller to fit the player. */
  height?: string
  children: React.ReactNode
}

/**
 * Shared chrome for every incoming-alert toast: the surface/border/shadow
 * card, the severity accent bar + icon, and the dismiss button. The four
 * notification cards supply only their `accentColor` and content.
 */
export const NotificationToastShell = ({
  accentColor,
  notificationTimestamp,
  onCancel,
  height = '110px',
  children,
}: NotificationToastShellIProps) => {
  const colorScheme = useLightMode()
  const tokens = getNotificationToastTokens(colorScheme)

  return (
    <Flex
      {...notificationToastShellStyles}
      bg={tokens.surface}
      borderColor={tokens.border}
      boxShadow={tokens.shadow}
      h={height}
    >
      <Flex bg={accentColor} h='full' w='10px' />
      <Box h='full' p='10px' position='relative' w='full'>
        <NotificationIcon color={accentColor} size='20px' />
        <Box ml='30px' position='relative'>
          {children}
        </Box>
      </Box>
      <IconButton
        aria-label='Dismiss notification'
        colorScheme={colorScheme}
        flexShrink={0}
        icon={<CloseIcon size={16} />}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          onCancel?.(notificationTimestamp)
        }}
      />
    </Flex>
  )
}
